import React from 'react';
import { ChangesRequestEntry } from '@/pages/Onboarding/NaturalPerson/RegistryDetails';
import MediaCard from '@/pages/Onboarding/MediaCard';
import { useTranslations } from 'use-intl';
import { RegistryDocuments, RegistryStatus } from '@/graphql/__generated__/onboarding';
import EmojiIcon from '@/stories/Components/EmojiIcon/EmojiIcon';
import Checkbox from '@/UIlibrary/Checkbox/Checkbox';

interface DocumentsSectionProps {
	documents: RegistryDocuments;
	changesRequested: Array<{ section: string; field: string; value: any }>;
	setChangesRequested: React.Dispatch<React.SetStateAction<ChangesRequestEntry[]>>;
	registryStatus: keyof typeof RegistryStatus;
}

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({
	documents,
	changesRequested,
	setChangesRequested,
	registryStatus
}) => {
	const t = useTranslations('RegistryDetails');

	const PROOF_OF_LIFE_FIELDS = [
		{ field: 'INE_FRONT', value: t('INE_FRONT') },
		{ field: 'INE_BACK', value: t('INE_BACK') },
		{ field: 'SELFIE', value: t('SELFIE') }
	];

	type FieldValue = {
		field: string;
		value: string;
	};

	const handleCheckboxChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		section: string,
		fields: FieldValue[]
	) => {
		const fieldsToChange = fields.map((item) => ({
			section,
			field: item.field,
			value: item.value
		}));
		if (e.target.checked) {
			setChangesRequested((prev) => [...prev, ...fieldsToChange]);
		} else {
			setChangesRequested((prev) =>
				prev.filter((change) => !fields.some((field) => field.field === change.field))
			);
		}
	};

	return (
		<div className="flex flex-col p-6 gap-5 bg-slate-50 p-4 rounded-lg">
			<div className="flex gap-5">
				<MediaCard
					translationKey="RegistryDetails"
					title={'INE'}
					files={[
						{
							url: documents.ineFront || '',
							label: 'ineFront'
						},
						{
							url: documents.ineBack || '',
							label: 'ineBack'
						}
					]}
					icon={<EmojiIcon src="/id-emoji.svg" />}
					checkbox={
						<div className="flex flex-col gap-2">
							<Checkbox
								name="ine"
								onChange={(e) => handleCheckboxChange(e, 'proofOfLife', PROOF_OF_LIFE_FIELDS)}
								checked={changesRequested.some((change) =>
									['INE_FRONT', 'INE_BACK', 'SELFIE'].includes(change.field)
								)}
								disabled={
									![RegistryStatus.COMPLETED, RegistryStatus.REJECTED].includes(
										registryStatus as RegistryStatus
									)
								}
							/>
						</div>
					}
				/>
				<MediaCard
					translationKey="RegistryDetails"
					title={'SELFIE'}
					files={[
						{
							url: documents.selfie || '',
							label: 'selfie'
						}
					]}
					icon={<EmojiIcon src="/selfie-emoji.svg" />}
					checkbox={
						<Checkbox
							name="selfie"
							onChange={(e) => handleCheckboxChange(e, 'proofOfLife', PROOF_OF_LIFE_FIELDS)}
							checked={changesRequested.some((change) =>
								['INE_FRONT', 'INE_BACK', 'SELFIE'].includes(change.field)
							)}
							disabled={
								![RegistryStatus.COMPLETED, RegistryStatus.REJECTED].includes(
									registryStatus as RegistryStatus
								)
							}
						/>
					}
				/>
				<MediaCard
					translationKey="RegistryDetails"
					title={t('ProofOfAddressFileTitle')}
					files={[
						{
							url: documents.proofOfAddress || '',
							label: 'proofOfAddress'
						}
					]}
					icon={<EmojiIcon src="/house-bushes-emoji.svg" />}
					checkbox={
						<Checkbox
							name="proofOfAddress"
							onChange={(e) =>
								handleCheckboxChange(e, 'officialAddress', [
									{ field: 'PROOF_OF_ADDRESS', value: t('File') }
								])
							}
							checked={changesRequested.some((change) => change.field === 'PROOF_OF_ADDRESS')}
							disabled={
								![RegistryStatus.COMPLETED, RegistryStatus.REJECTED].includes(
									registryStatus as RegistryStatus
								)
							}
						/>
					}
				/>
				<MediaCard
					translationKey="RegistryDetails"
					title={t('TaxCertificateFileTitle')}
					files={[
						{
							url: documents.taxStatusCertificate || '',
							label: 'taxCertificate'
						}
					]}
					icon={<EmojiIcon src="/document-emoji.svg" />}
					checkbox={
						<Checkbox
							name="taxCertificate"
							onChange={(e) =>
								handleCheckboxChange(e, 'taxInformation', [
									{ field: 'TAX_STATUS_CERTIFICATE', value: t('File') }
								])
							}
							checked={changesRequested.some((change) => change.field === 'TAX_STATUS_CERTIFICATE')}
							disabled={true}
						/>
					}
				/>
			</div>
		</div>
	);
};

export default DocumentsSection;
